import { SITE_INTEGRATION_TYPE } from "@shared/common/constant/site";
import { exhaustiveGuard } from "@shared/common/utils/exhaustive-guard";
import { z } from "zod";
import type { SiteIntegrationInfo } from "../utils/integrations";

/** node_modules/@medusajs/utils/dist/product/index.d.ts */
export enum ProductStatus {
  DRAFT = "draft",
  PROPOSED = "proposed",
  PUBLISHED = "published",
  REJECTED = "rejected",
}

export const productStatusSchema = z.enum([
  ProductStatus.DRAFT,
  ProductStatus.PROPOSED,
  ProductStatus.PUBLISHED,
  ProductStatus.REJECTED,
]);

export const medusaProductCategorySchema = z.object({
  id: z.string(),
  vendor_link: z
    .object({
      // vendor_id: z.string(),
      // product_category_id: z.string(),
      // id: z.string(),
      original_category_id: z.string(),
      full_category_name: z.string(),
      // created_at: z.string(),
      // updated_at: z.string(),
      // deleted_at: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
});

export const medusaProductExternalSchema = z.object({
  id: z.string(),
  original_id: z.string(),
  product_code: z.string(),
  original_updated_at: z.string(),
  original_created_at: z.string(),
  url: z.string().optional().nullable(),
  // created_at: z.string(),
  // updated_at: z.string(),
  // deleted_at: z.string().optional().nullable(),
});

export const medusaProductSchema = z.object({
  id: z.string(),
  title: z.string(),
  // handle: z.string(),
  // subtitle: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  // is_giftcard: z.boolean(),
  // status: productStatusSchema,
  thumbnail: z.string().optional().nullable(),
  // weight: z.string().optional().nullable(),
  // length: z.string().optional().nullable(),
  // height: z.string().optional().nullable(),
  // width: z.string().optional().nullable(),
  // origin_country: z.string().optional().nullable(),
  // hs_code: z.string().optional().nullable(),
  // mid_code: z.string().optional().nullable(),
  // material: z.string().optional().nullable(),
  // collection_id: z.string().optional().nullable(),
  // collection: z.string().optional().nullable(),
  // type_id: z.string().optional().nullable(),
  // discountable: z.boolean(),
  external_id: z.string(),
  // created_at: z.string(),
  // updated_at: z.string(),
  // deleted_at: z.string().optional().nullable(),
  metadata: z
    .object({
      external_price: z
        .object({
          price: z.string().optional().nullable(),
          discount_price: z.string().optional().nullable(),
          retail_price: z.string().optional().nullable(),
          supply_price: z.string().optional().nullable(),
          currency: z.string().optional().nullable(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
  // type: z.string().optional().nullable(),
  categories: z.array(medusaProductCategorySchema).optional().nullable(),
  product_external: medusaProductExternalSchema.optional().nullable(),
});

export enum VendorType {
  Cafe24 = "cafe24",
  Shopify = "shopify",
  Sixshop = "sixshop",
  GFore = "gfore",
  Imweb = "imweb",
  Godomall = "godomall",
  Custom = "custom",
  SixshopPro = "sixshop_pro",
  Makeshop = "makeshop",
}

export const vendorTypeSchema = z.enum([
  VendorType.Cafe24,
  VendorType.Shopify,
  VendorType.Sixshop,
  VendorType.GFore,
  VendorType.Imweb,
  VendorType.Godomall,
  VendorType.Custom,
  VendorType.SixshopPro,
  VendorType.Makeshop,
]);

export type VendorInfo = {
  vendorType: VendorType;
  originalStoreId: string;
  originalSubstoreId: string | null;
};

export const getVendorInfoBySiteIntegration = (
  integration: SiteIntegrationInfo
): VendorInfo => {
  switch (integration.type) {
    case SITE_INTEGRATION_TYPE.CAFE24:
      return {
        vendorType: VendorType.Cafe24,
        originalStoreId: integration.mallId,
        originalSubstoreId: integration.shopNo,
      };
    case SITE_INTEGRATION_TYPE.SHOPIFY:
      return {
        vendorType: VendorType.Shopify,
        originalStoreId: integration.shopName,
        originalSubstoreId: null,
      };
    case SITE_INTEGRATION_TYPE.SIXSHOP_PRO:
      return {
        vendorType: VendorType.SixshopPro,
        originalStoreId: integration.storeId,
        originalSubstoreId: null,
      };
    case SITE_INTEGRATION_TYPE.GODOMALL:
      return {
        vendorType: VendorType.Godomall,
        originalStoreId: integration.mallNo,
        originalSubstoreId: null,
      };
    case SITE_INTEGRATION_TYPE.IMWEB:
      return {
        vendorType: VendorType.Imweb,
        originalStoreId: integration.siteCode,
        originalSubstoreId: null, // TODO: unitCode 추가?
      };
    case SITE_INTEGRATION_TYPE.GFORE: {
      return {
        vendorType: VendorType.GFore,
        originalStoreId: "gfore", // TODO: 수정
        originalSubstoreId: null,
      };
    }
    case SITE_INTEGRATION_TYPE.MAKESHOP: {
      return {
        vendorType: VendorType.Makeshop,
        originalStoreId: integration.mallId,
        originalSubstoreId: null,
      };
    }
    case SITE_INTEGRATION_TYPE.SIXSHOP:
    case SITE_INTEGRATION_TYPE.NHN:
    case SITE_INTEGRATION_TYPE.WORDPRESS:
      throw new Error("Not implemented");
    default:
      return exhaustiveGuard(integration);
  }
};

export enum SyncStatus {
  Synced = "synced",
  Syncing = "syncing",
  Error = "error",
  Stopped = "stopped",
}

export enum SyncType {
  Product = "product",
  Order = "order",
  Customer = "customer",
  ProductCategory = "product_category",
}

export const syncStatusSchema = z.enum([
  SyncStatus.Synced,
  SyncStatus.Syncing,
  SyncStatus.Error,
  SyncStatus.Stopped,
]);

export const syncTypeSchema = z.enum([
  SyncType.Product,
  SyncType.Order,
  SyncType.Customer,
  SyncType.ProductCategory,
]);

export type Product = z.infer<typeof medusaProductSchema>;
